import { UPLOAD_TYPES, isKnownMarketPair, isKnownBrokerName } from './uploadTypes';

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const MAX_ROWS = 5000;

export function validateHeaders(uploadType, headerRow) {
  const type = UPLOAD_TYPES[uploadType];
  const normalized = (headerRow || []).map((h) => String(h || '').trim().toLowerCase().replace(/\s+/g, '_'));
  const missing = type.columns.filter((col) => !normalized.includes(col));
  return { normalized, missing };
}

function isRowIncomplete(row, type) {
  return type.requiredColumns.some((col) => !String(row[col] || '').trim());
}

export function validateRows(uploadType, rows, options = {}) {
  const type = UPLOAD_TYPES[uploadType];
  const { validVendorNumbers = null } = options;

  if (rows.length > MAX_ROWS) {
    return {
      validRows: [],
      errors: [{ rowNumber: null, contactEmail: null, reason: `File has ${rows.length} rows, which exceeds the ${MAX_ROWS} row limit` }],
      distinctVendorNumbers: [],
    };
  }

  const validRows = [];
  const errors = [];
  const seenKeys = new Map();
  const distinctVendorNumbers = new Set();

  rows.forEach((row, index) => {
    const rowNumber = index + 2;

    if (isRowIncomplete(row, type)) {
      return;
    }

    const mapped = {};
    let rowError = null;

    const marketName = String(row.market_name || '').trim();
    const marketNumberRaw = String(row.market_number || '').trim();
    const marketNumber = Number(marketNumberRaw);
    if (!marketName || !marketNumberRaw || !Number.isFinite(marketNumber) || !isKnownMarketPair(marketName, marketNumber)) {
      rowError = 'Market Name / Market Number does not match a known market';
    } else {
      mapped.market_number = marketNumber;
    }

    if (!rowError && type.columns.includes('vendor_number')) {
      const raw = String(row.vendor_number || '').trim();
      if (!raw) {
        rowError = 'Missing vendor number';
      } else if (!/^\d+$/.test(raw)) {
        rowError = 'Vendor Number must be numeric';
      } else {
        mapped.vendor_number = Number(raw);
        distinctVendorNumbers.add(mapped.vendor_number);
      }
    }

    if (!rowError && type.columns.includes('primary_contact_name')) {
      const raw = String(row.primary_contact_name || '').trim();
      if (!raw) {
        rowError = 'Missing primary contact name';
      } else {
        mapped.primary_contact_name = raw;
      }
    }

    if (!rowError && type.columns.includes('primary_contact_email')) {
      const raw = String(row.primary_contact_email || '').trim();
      if (!raw) {
        rowError = 'Missing primary contact email';
      } else if (!EMAIL_REGEX.test(raw)) {
        rowError = 'Invalid email format';
      } else {
        mapped.primary_contact_email = raw;
      }
    }

    if (!rowError && type.columns.includes('primary_contact_phone')) {
      const digits = String(row.primary_contact_phone || '').replace(/\D/g, '');
      if (digits) {
        mapped.primary_contact_phone = digits;
      }
    }

    if (!rowError && type.columns.includes('broker_name')) {
      const raw = String(row.broker_name || '').trim();
      if (!raw) {
        rowError = 'Missing broker name';
      } else if (!isKnownBrokerName(raw)) {
        rowError = 'Broker Name is not one of the allowed values';
      } else {
        mapped.broker_name = raw;
      }
    }

    if (!rowError && validVendorNumbers && 'vendor_number' in mapped && !validVendorNumbers.includes(mapped.vendor_number)) {
      rowError = 'Vendor Number does not belong to your account';
    }

    const contactEmail = mapped.primary_contact_email || null;

    if (rowError) {
      errors.push({ rowNumber, contactEmail, reason: rowError });
      return;
    }

    const keyValue = type.naturalKey.map((k) => mapped[k]).join('|');
    if (seenKeys.has(keyValue)) {
      errors.push({ rowNumber, contactEmail, reason: `Duplicate of row ${seenKeys.get(keyValue)}` });
      return;
    }
    seenKeys.set(keyValue, rowNumber);

    validRows.push({ ...mapped, rowNumber });
  });

  const distinctVendorNumbersArr = Array.from(distinctVendorNumbers);
  if (distinctVendorNumbersArr.length > 1) {
    return {
      validRows: [],
      errors: [{ rowNumber: null, contactEmail: null, reason: 'This file contains more than one Vendor Number — please submit a separate file for each vendor' }],
      distinctVendorNumbers: distinctVendorNumbersArr,
    };
  }

  return { validRows, errors, distinctVendorNumbers: distinctVendorNumbersArr };
}
